import React, { useState, useEffect } from 'react';
import { FiShoppingBag, FiTruck, FiDollarSign, FiDownload, FiPlus, FiMessageSquare, FiRefreshCw, FiStar, FiBell, FiGift } from 'react-icons/fi';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';
import styles from './Dashboard.module.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const WholesaleOverview = ({ customer }) => {
  const isTrialAccount = customer?.status === 'trial';
  const isDemoAccount = customer?.status === 'demo';
  const trialDaysLeft = isTrialAccount && customer?.trialEndDate 
    ? Math.ceil((new Date(customer.trialEndDate.seconds * 1000) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const demoDaysLeft = isDemoAccount && customer?.demoEndDate 
    ? Math.ceil((new Date(customer.demoEndDate.seconds * 1000) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // Statistics state
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    activeOrders: 0,
    creditUsed: 0,
    creditAvailable: 0,
    creditLimit: 0,
    loading: true
  });

  // Chart data state
  const [chartData, setChartData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        fill: true,
        label: 'Monthly Orders',
        data: [0, 0, 0, 0, 0, 0],
        borderColor: '#1f2937',
        backgroundColor: 'rgba(31, 41, 55, 0.05)',
        tension: 0.4,
        pointBackgroundColor: '#1f2937',
        pointBorderColor: '#fff',
        pointHoverRadius: 6,
        pointHoverBorderWidth: 2,
      }
    ]
  });

  // Recent orders state
  const [recentOrders, setRecentOrders] = useState([]);
  
  // Frequently ordered items state
  const [frequentItems, setFrequentItems] = useState([]);
  
  // Mobile detection state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch real statistics from Firebase
  useEffect(() => {
    const fetchStats = async () => {
      if (!customer?.id) return;
      
      try {
        // Fetch orders for this customer
        const ordersQuery = query(
          collection(db, 'wholesaleOrders'),
          where('customerId', '==', customer.id),
          orderBy('createdAt', 'desc')
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Set recent orders (last 3 orders)
        setRecentOrders(orders.slice(0, 3));
        
        // Calculate frequently ordered items
        const itemFrequency = {};
        orders.forEach(order => {
          if (order.items && Array.isArray(order.items)) {
            order.items.forEach(item => {
              if (itemFrequency[item.name]) {
                itemFrequency[item.name].count += item.quantity || 1;
                itemFrequency[item.name].lastOrder = order.createdAt;
              } else {
                itemFrequency[item.name] = {
                  name: item.name,
                  count: item.quantity || 1,
                  lastOrder: order.createdAt
                };
              }
            });
          }
        });
        
        // Convert to array and sort by frequency
        const frequentItemsArray = Object.values(itemFrequency)
          .sort((a, b) => b.count - a.count)
          .slice(0, 3)
          .map(item => ({
            name: item.name,
            orders: item.count,
            lastOrder: item.lastOrder ? 
              new Date(item.lastOrder.seconds * 1000).toLocaleDateString() : 
              'N/A'
          }));
        
        setFrequentItems(frequentItemsArray);
        
        // Calculate statistics
        const totalOrders = orders.length;
        const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
        const activeOrders = orders.filter(order => 
          ['pending', 'processing', 'shipped'].includes(order.status)
        ).length;
        
        // Get credit information from customer data
        const creditLimit = customer.creditLimit || 5000; // Default 5000 QAR
        const creditUsed = customer.creditUsed || 0;
        const creditAvailable = creditLimit - creditUsed;
        
        // Create chart data from last 6 months
        const monthlyData = Array(6).fill(0);
        const monthLabels = [];
        const now = new Date();
        
        // Generate last 6 months labels
        for (let i = 5; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          monthLabels.push(date.toLocaleDateString('en-US', { month: 'short' }));
        }
        
        // Count orders per month
        orders.forEach(order => {
          if (order.createdAt) {
            const orderDate = order.createdAt.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
            const monthsAgo = Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
            if (monthsAgo >= 0 && monthsAgo < 6) {
              monthlyData[5 - monthsAgo]++;
            }
          }
        });
        
        setChartData({
          labels: monthLabels,
          datasets: [
            {
              fill: true,
              label: 'Monthly Orders',
              data: monthlyData,
              borderColor: '#1f2937',
              backgroundColor: 'rgba(31, 41, 55, 0.05)',
              tension: 0.4,
              pointBackgroundColor: '#1f2937',
              pointBorderColor: '#fff',
              pointHoverRadius: 6,
              pointHoverBorderWidth: 2,
            }
          ]
        });
        
        setStats({
          totalOrders,
          totalRevenue,
          activeOrders,
          creditUsed,
          creditAvailable,
          creditLimit,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching statistics:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, [customer?.id]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          padding: 16,
          color: '#64748b',
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#fff',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: isMobile ? 8 : 12,
        titleFont: {
          size: isMobile ? 11 : 13
        },
        bodyFont: {
          size: isMobile ? 10 : 12
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US').format(context.parsed.y);
            }
            return ' ' + label;
          }
        }
      }
    },
    scales: {
      y: { 
        beginAtZero: true, 
        grid: { 
          color: '#f1f5f9',
          drawBorder: false,
        },
        ticks: {
          color: '#94a3b8',
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      x: { 
        grid: { 
          display: false 
        },
        ticks: {
          color: '#9ca3af',
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
    },
    interaction: {
        mode: 'index',
        intersect: false,
    },
    // Add touch-friendly interactions
    onHover: (event, elements) => {
      if (elements.length > 0) {
        event.native.target.style.cursor = 'pointer';
      } else {
        event.native.target.style.cursor = 'default';
      }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        // Handle chart clicks for mobile
        console.log('Chart clicked:', elements[0]);
      }
    }
  };

  const quickActions = [
    { icon: <FiPlus />, text: 'Place New Order' },
    { icon: <FiDownload />, text: 'Download Invoice' },
    { icon: <FiMessageSquare />, text: 'Contact Support' },
    { icon: <FiStar />, text: 'Request Product' },
  ];

  return (
    <>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>Hello, {customer?.businessName}</div>
          <div className={styles.headerSubtitle}>Welcome to the Wholesale Management Dashboard.</div>
        </div>
        <div className={styles.headerProfile} style={{ display: isMobile ? 'none' : 'flex' }}>
          <div style={{ position: 'relative', marginRight: 12 }}>
            <FiBell style={{ fontSize: '1.2rem', color: '#64748b' }} />
            <div style={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 8,
              height: 8,
              background: '#ef4444',
              borderRadius: '50%'
            }}></div>
          </div>
          <div className={styles.profileAvatar}>{customer?.businessName?.[0]}</div>
          <span className={styles.profileRole}>Admin</span>
        </div>
      </div>

      {/* Demo Account Banner */}
      {isDemoAccount && (
        <div className={styles.promoBannerContainer}>
          <div className={styles.promoBanner} style={{ backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' }}>
            <FiBell style={{ fontSize: '1.2rem', flexShrink: 0 }} />
            <div className={styles.promoText}>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>👁️ Demo Account - {demoDaysLeft} days remaining</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>View-only access • See our products & pricing</div>
            </div>
            <button className={styles.promoButton} style={{ backgroundColor: '#1565c0', color: '#fff' }}>
              Request Full Access
            </button>
          </div>
        </div>
      )}

      {/* Trial Account Banner */}
      {isTrialAccount && (
        <div className={styles.promoBannerContainer}>
          <div className={styles.promoBanner} style={{ backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' }}>
            <FiBell style={{ fontSize: '1.2rem', flexShrink: 0 }} />
            <div className={styles.promoText}>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>🔔 Trial Account - {trialDaysLeft} days remaining</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Max QAR 1000 per order • 3 orders per month limit</div>
            </div>
            <button className={styles.promoButton} style={{ backgroundColor: '#856404', color: '#fff' }}>
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {/* Promotions Banner */}
      {!isTrialAccount && !isDemoAccount && (
        <div className={styles.promoBannerContainer}>
          <div className={styles.promoBanner}>
            <FiGift style={{ fontSize: '1.2rem', flexShrink: 0 }} />
            <div className={styles.promoText}>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>🎉 Limited Time: 15% off all Ethiopian Origins</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Valid until June 30th • Use code: ETHIOPIA15</div>
            </div>
            <button className={styles.promoButton}>
              View Deal
            </button>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className={styles.dashboardGrid}>
        {/* Stats Column */}
        <div className={styles.statsCol}>
          <div className={styles.statCard}>
            <div className={styles.statTitle}>Total Orders</div>
            <div className={styles.statValue}>{stats.loading ? 'Loading...' : stats.totalOrders}</div>
            <div className={styles.statChange}>All Time</div>
            <div className={styles.statSub}>Orders Placed</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statTitle}>Total Revenue</div>
            <div className={styles.statValue}>{stats.loading ? 'Loading...' : `QAR ${stats.totalRevenue.toFixed(2)}`}</div>
            <div className={styles.statChange}>All Time</div>
            <div className={styles.statSub}>Total Spent</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statTitle}>Active Orders</div>
            <div className={styles.statValue}>{stats.loading ? 'Loading...' : stats.activeOrders}</div>
            <div className={styles.statChange}>In Progress</div>
            <div className={styles.statSub}>Pending/Processing</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statTitle}>Credit Terms</div>
            <div className={styles.statValue}>{stats.loading ? 'Loading...' : `QAR ${stats.creditUsed.toFixed(2)}`}</div>
            <div className={styles.statChange}>Credit Used</div>
            <div className={styles.creditDetails}>
              <span className={styles.creditAvailable}>Available: QAR {stats.creditAvailable.toFixed(2)}</span>
              <span className={styles.creditDue}>Limit: QAR {stats.creditLimit.toFixed(2)}</span>
            </div>
            <div className={styles.creditBar}>
              <div className={styles.creditBarUsed} style={{ width: `${stats.creditLimit > 0 ? (stats.creditUsed / stats.creditLimit * 100).toFixed(1) : 0}%` }}></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className={styles.card}>
            <div className={styles.cardTitleRow}>
              <div className={styles.cardTitle}>Quick Actions</div>
            </div>
            <div className={styles.quickActions}>
              {quickActions.map((action, index) => (
                <button 
                  key={index} 
                  className={styles.quickActionBtn}
                  style={{
                    minHeight: isMobile ? '60px' : 'auto',
                    padding: isMobile ? '16px' : '20px'
                  }}
                >
                  <div className={styles.quickActionIcon}>
                    {action.icon}
                  </div>
                  {action.text}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Cards Column */}
        <div className={styles.cardsCol}>
          <div className={styles.card}>
            <div className={styles.cardTitleRow}>
              <div className={styles.cardTitle}>Orders Statistics</div>
              <select className={styles.chartPeriodSelect}>
                <option>Monthly</option>
                <option>Weekly</option>
                <option>Daily</option>
              </select>
            </div>
            <div style={{height: isMobile ? 200 : 250}}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Frequently Ordered Items */}
          <div className={styles.card}>
            <div className={styles.cardTitleRow}>
              <div className={styles.cardTitle}>Frequently Ordered</div>
              <button className={styles.cardAction}>View All</button>
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
              {frequentItems.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#64748b',
                  fontSize: '0.9rem'
                }}>
                  No frequently ordered items yet
                </div>
              ) : (
                frequentItems.map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #f1f5f9'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{item.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        {item.orders} orders • Last: {item.lastOrder}
                      </div>
                    </div>
                    <button 
                      className={`${styles.listButton} ${styles.listButtonPrimary}`}
                      style={{
                        minHeight: isMobile ? '44px' : 'auto',
                        minWidth: isMobile ? '44px' : 'auto'
                      }}
                    >
                      <FiRefreshCw size={12} />
                      Reorder
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Enhanced Recent Orders */}
          <div className={styles.card}>
            <div className={styles.cardTitleRow}>
              <div className={styles.cardTitle}>Recent Orders</div>
              <button className={styles.cardAction}>See All</button>
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
              {recentOrders.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#64748b',
                  fontSize: '0.9rem'
                }}>
                  No recent orders found
                </div>
              ) : (
                recentOrders.map((order) => (
                  <div key={order.id} className={styles.listItem}>
                    <div>
                      <div style={{fontWeight: 600}}>Order #{order.orderNumber || order.id.slice(0, 8)}</div>
                      <div style={{fontSize: '0.9rem', color: '#64748b'}}>
                        {order.status || 'Pending'} • QAR {(order.total || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className={styles.listButtonSet}>
                      <button 
                        className={`${styles.listButton} ${
                          order.status === 'pending' ? styles.listButtonMuted : styles.listButtonSecondary
                        }`}
                        style={{
                          minHeight: isMobile ? '44px' : 'auto',
                          minWidth: isMobile ? '44px' : 'auto'
                        }}
                      >
                        {order.status === 'pending' ? 'Pending' : 'Track'}
                      </button>
                      <button 
                        className={`${styles.listButton} ${styles.listButtonPrimary}`}
                        style={{
                          minHeight: isMobile ? '44px' : 'auto',
                          minWidth: isMobile ? '44px' : 'auto'
                        }}
                      >
                        <FiRefreshCw size={10} />
                        Reorder
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WholesaleOverview; 